import { create } from 'zustand';
import type { MeResponse } from '@castinflow/shared';
import { ApiError, apiFetch, csrfToken } from './api';

/** Çerez tabanlı oturum: mutasyonlarda çift-gönderim CSRF başlığı eklenir (belirteç kaynağı: lib/api.ts csrfToken). */
export function csrfHeaders(): Record<string, string> {
  const token = csrfToken();
  return token ? { 'x-csrf-token': token } : {};
}

export async function apiMutate<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
  /** Ek başlıklar (ör. `Idempotency-Key`); CSRF ve içerik tipi her zaman eklenir */
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  return apiFetch<T>(path, {
    method,
    headers: { 'content-type': 'application/json', ...csrfHeaders(), ...extraHeaders },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

interface SessionState {
  me: MeResponse | null;
  status: 'unknown' | 'anonymous' | 'authenticated';
  load: () => Promise<void>;
  /** Oturum açıkken `/auth/me` tazelemesi (yetenek değişikliği); geçici ağ hatası oturumu düşürmez, 401 düşürür */
  refresh: () => Promise<void>;
  setMe: (me: MeResponse | null) => void;
  logout: () => Promise<void>;
}

/** Plan §5: yalnız bellekte; çıkış/tenant değişiminde temizlenir (QueryClient temizliği App'te). */
export const useSession = create<SessionState>((set, get) => ({
  me: null,
  status: 'unknown',
  load: async () => {
    try {
      const me = await apiFetch<MeResponse>('/auth/me');
      set({ me, status: 'authenticated' });
    } catch {
      set({ me: null, status: 'anonymous' });
    }
  },
  refresh: async () => {
    if (get().status !== 'authenticated') return;
    try {
      const me = await apiFetch<MeResponse>('/auth/me');
      set({ me, status: 'authenticated' });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) set({ me: null, status: 'anonymous' });
    }
  },
  setMe: (me) => {
    set({ me, status: me ? 'authenticated' : 'anonymous' });
  },
  logout: async () => {
    try {
      await apiMutate('/auth/logout', 'POST');
    } finally {
      set({ me: null, status: 'anonymous' });
    }
  },
}));
